'use client'

import { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useRouter } from '@/i18n/navigation'
import Image from 'next/image'
import {
  Menu,
  Search,
  X,
  ChevronDown,
  ChevronRight,
  User,
  Lock,
  // Community / Resources mega-menu card icons. Each picked to give a
  // visual hint at a glance — Sparkles for the live discovery feed,
  // Camera for monthly photo contests, etc.
  Sparkles,
  Camera,
  MessagesSquare,
  BookOpen,
  Compass,
  BookMarked,
  HelpCircle,
  GraduationCap,
  Clapperboard,
  Folder,
  CreditCard,
  Handshake,
  Users,
  Presentation,
  Vote,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { MegaMenuPanel } from './mega-menu-panel'
import { LanguageSwitcher } from './language-switcher'
import { useTranslations } from 'next-intl'
import { createClient } from '@/lib/supabase/client'
import { getVerifiedClaims, claimsToUserShape } from '@/lib/supabase/claims'
import type { User as SupabaseUser } from '@supabase/supabase-js'

// ---- Quick-peek search types & helpers ----
interface QuickPeekResult {
  id: string
  type: string
  slug: string
  title: string
  thumbnailUrl: string | null
  isGated: boolean
}

const PEEK_TYPE_LABELS: Record<string, string> = {
  tutorial: 'Tutorial',
  cas: 'Creating a Scene',
  floggerDesign: 'Flogger Design',
  floggerTool: 'Tool',
  floggerFoundationalKnot: 'Foundational Knot',
  floggerHandle: 'Handle',
  floggerTransitionKnot: 'Transition Knot',
  floggerFallKnot: 'Fall Knot',
  post: 'Blog',
  course: 'Course',
  // content_index normalizes every flogger sub-type to 'flogger' and also
  // indexes lessons — both must be labeled or they fall back to the raw type.
  flogger: 'Flogger',
  lesson: 'Lesson',
}

const PEEK_TYPE_ROUTES: Record<string, string> = {
  tutorial: '/tutorials',
  cas: '/creating-a-scene',
  floggerDesign: '/floggers',
  floggerTool: '/floggers/tools',
  floggerFoundationalKnot: '/floggers/foundational-knots',
  floggerHandle: '/floggers/handles',
  floggerTransitionKnot: '/floggers/transition-knots',
  floggerFallKnot: '/floggers/fall-knots',
  post: '/blogs',
  course: '/courses',
  // content_index type for any flogger is 'flogger'; lessons resolve via
  // /lessons/[slug] (which redirects into the owning course lesson).
  flogger: '/floggers',
  lesson: '/lessons',
}

// Build a quick-peek result href. Guards against an unmapped type producing a
// protocol-relative `//slug` link — the browser resolves `//silence-does-not-
// mean-consent` to the host https://silence-does-not-mean-consent/, a dead
// external link. Unmapped types fall back to the full search page.
function peekResultHref(type: string, slug: string): string {
  const base = PEEK_TYPE_ROUTES[type]
  return base ? `${base}/${slug}` : '/search'
}

export interface NavItem {
  label: string
  href: string
  panel?: {
    tabs: { label: string; href: string }[]
    featured?: { title: string; href: string; thumbnail?: string; author?: string; isGated?: boolean; category?: string }[]
    banners?: { label: string; href: string; backgroundImage?: string }[]
    /** Split layout: category links on left, featured cards on right */
    categories?: { label: string; description: string; href: string }[]
    /** Column layout: each column is a category with its own featured
     *  items. `key` is the stable difficulty value used to key the
     *  cas-featured data lookup (distinct from the translated label). */
    columns?: { key: string; label: string; href: string; color: string }[]
    /** Sections layout: side-by-side labeled groups of links. `key` is
     *  the stable, untranslated section id the desktop panel keys its
     *  columns off ('body-parts' | 'difficulty' | 'specialty'). */
    sections?: { key?: string; label: string; items: { label: string; href: string; color?: string; image?: string }[] }[]
    /** Cards layout: grid of label + description tiles (community / resources) */
    cards?: {
      label: string
      description: string
      href: string
      icon?: React.ComponentType<{ className?: string }>
    }[]
    /** Flogger layout: two-column designs + knots */
    floggerLayout?: boolean
    /** Store layout: digital + physical + affiliates split */
    storeLayout?: boolean
    /** Courses layout: 3 columns by difficulty (beginner / int / adv) */
    coursesLayout?: boolean
  }
  dropdown?: { label: string; href: string }[]
  noSkeleton?: boolean
}

/**
 * Builds the mega-menu navigation tree with locale-aware labels.
 * Called inside the SiteHeader function so `t` resolves at render
 * time. The hrefs / panel layout / colors are static config; only
 * the label strings move through translation.
 */
function buildNavItems(t: (key: string) => string): NavItem[] {
  return [
    {
      label: t('tutorials'),
      href: '/tutorials',
      panel: {
        tabs: [],
        sections: [
          {
            key: 'body-parts',
            label: t('byBodyPart'),
            items: [
              {
                label: t('arms'),
                href: '/tutorials?bodyPart=arms',
                image:
                  'https://cdn.sanity.io/images/vedbawa9/production/ebc03a3987756d984d209e1351f65c20a1e1c4c4-2358x2560.jpg',
              },
              {
                label: t('chest'),
                href: '/tutorials?bodyPart=chest',
                image:
                  'https://cdn.sanity.io/images/vedbawa9/production/1dc59529cd4b2149070d731ff6b9f7554d5f3ffb-2000x2000.jpg',
              },
              {
                label: t('waist'),
                href: '/tutorials?bodyPart=waist',
                image:
                  'https://cdn.sanity.io/images/vedbawa9/production/0cfeac67e9324dbc11b05c680cc98610007a4eca-2000x1476.jpg',
              },
              {
                label: t('hips'),
                href: '/tutorials?bodyPart=hips',
                image:
                  'https://cdn.sanity.io/images/vedbawa9/production/c4d5bc9a0b3d1f96ff40519c8bed9c8b4a0a43aa-2345x2000.jpg',
              },
              {
                label: t('legs'),
                href: '/tutorials?bodyPart=legs',
                image:
                  'https://cdn.sanity.io/images/vedbawa9/production/3843ddff96d047d67aadc6aced2044ae0894c0e4-2000x1333.jpg',
              },
              {
                label: t('fullBody'),
                href: '/tutorials?bodyPart=full-body',
                image:
                  'https://cdn.sanity.io/images/vedbawa9/production/52f566925df60d5159fe1e355fdb96dbe030004b-2000x2000.jpg',
              },
            ],
          },
          {
            key: 'difficulty',
            label: t('byDifficulty'),
            items: [
              // URL param reads "easy" for the easiest tier (client
              // request); the stored difficulty value is still
              // 'beginner'.
              { label: t('beginner'), href: '/tutorials?difficulty=easy', color: '#16a34a' },
              { label: t('intermediate'), href: '/tutorials?difficulty=intermediate', color: '#d97706' },
              { label: t('advanced'), href: '/tutorials?difficulty=advanced', color: '#dc2626' },
            ],
          },
          {
            // Specialty — the signature styles/disciplines. One row per
            // value in the tutorial schema's `specialty` options list;
            // adding the next specialty is one more entry here. Each
            // link lands on the tutorials grid pre-filtered via the
            // `?specialty=` URL param (see tutorial-grid.tsx).
            key: 'specialty',
            label: t('bySpecialty'),
            items: [
              { label: t('specialtyFashion'), href: '/tutorials?specialty=fashion-shibari' },
              { label: t('specialtySelfTie'), href: '/tutorials?specialty=self-tie' },
              { label: t('specialtySuspension'), href: '/tutorials?specialty=suspension' },
              { label: t('specialtyAccessibility'), href: '/tutorials?specialty=accessibility' },
              { label: t('specialtyDrills'), href: '/tutorials?specialty=drills' },
            ],
          },
        ],
      },
    },
    {
      label: t('courses'),
      href: '/courses',
      panel: {
        tabs: [],
        // Courses panel mirrors the floggers card-grid layout: 3
        // columns by difficulty, each with thumbnail cards instead of
        // the previous tabs-only filter strip.
        coursesLayout: true,
        // `columns` is mobile-only here: the desktop dispatch returns
        // CoursesLayout (checked before `columns`), so these difficulty
        // links only render in the mobile pull-out drawer.
        columns: [
          { key: 'beginner', label: t('beginner'), href: '/courses?difficulty=easy', color: '#16a34a' },
          { key: 'intermediate', label: t('intermediate'), href: '/courses?difficulty=intermediate', color: '#d97706' },
          { key: 'advanced', label: t('advanced'), href: '/courses?difficulty=advanced', color: '#dc2626' },
        ],
      },
    },
    {
      label: t('creatingAScene'),
      href: '/creating-a-scene',
      panel: {
        tabs: [],
        // `key` is the stored difficulty value — drives the
        // cas-featured data lookup in ColumnLayout. The URL param
        // reads "easy" for the easiest tier (client request) even
        // though the stored value stays 'beginner'.
        columns: [
          { key: 'beginner', label: t('beginner'), href: '/creating-a-scene?difficulty=easy', color: '#16a34a' },
          { key: 'intermediate', label: t('intermediate'), href: '/creating-a-scene?difficulty=intermediate', color: '#d97706' },
          { key: 'advanced', label: t('advanced'), href: '/creating-a-scene?difficulty=advanced', color: '#dc2626' },
        ],
      },
    },
    {
      label: t('floggers'),
      href: '/floggers',
      panel: {
        // `tabs` is mobile-only here: the desktop dispatch returns
        // FloggerLayout (checked before the `tabs` fallback), so these
        // category links only render in the mobile pull-out drawer.
        tabs: [
          { label: t('floggerDesigns'), href: '/floggers' },
          { label: t('floggerTools'), href: '/floggers/tools' },
          { label: t('floggerFoundationalKnots'), href: '/floggers/foundational-knots' },
          { label: t('floggerHandles'), href: '/floggers/handles' },
          { label: t('floggerTransitionKnots'), href: '/floggers/transition-knots' },
          { label: t('floggerFallKnots'), href: '/floggers/fall-knots' },
        ],
        floggerLayout: true,
      },
    },
    {
      label: t('blog'),
      href: '/blogs',
      panel: {
        tabs: [],
        categories: [
          { label: t('general'), description: t('generalDescription'), href: '/blogs/general' },
          { label: t('spicyDateNights'), description: t('spicyDateNightsDescription'), href: '/blogs/spicy-date-nights' },
          { label: t('dbrt'), description: t('dbrtDescription'), href: '/blogs/diary-of-a-baby-rope-top' },
        ],
      },
    },
    {
      label: t('community'),
      href: '/community',
      panel: {
        tabs: [],
        cards: [
          {
            label: t('discoveryFeed'),
            description: t('discoveryFeedDescription'),
            href: '/community',
            icon: Sparkles,
          },
          {
            label: t('photoContests'),
            description: t('photoContestsDescription'),
            href: '/contests',
            icon: Camera,
          },
          {
            label: t('discord'),
            description: t('discordDescription'),
            href: '/discord',
            icon: MessagesSquare,
          },
        ],
      },
      noSkeleton: true,
    },
    {
      label: t('store'),
      href: '/store',
      panel: {
        tabs: [
          { label: t('digitalProducts'), href: '/store#digital' },
          { label: t('physicalProducts'), href: '/store#physical' },
          { label: t('partners'), href: '/store#partners' },
        ],
        storeLayout: true,
      },
    },
    {
      label: t('resources'),
      href: '/resources',
      panel: {
        tabs: [],
        cards: [
          {
            label: t('newToShibari'),
            description: t('newToShibariDescription'),
            href: '/new-to-shibari',
            icon: BookOpen,
          },
          {
            label: t('gettingStarted'),
            description: t('gettingStartedDescription'),
            href: '/getting-started',
            icon: Compass,
          },
          {
            label: t('glossary'),
            description: t('glossaryDescription'),
            href: '/glossary',
            icon: BookMarked,
          },
          {
            label: t('faq'),
            description: t('faqDescription'),
            href: '/faq',
            icon: HelpCircle,
          },
          {
            label: t('instructorResources'),
            description: t('instructorResourcesDescription'),
            href: '/instructor-resources',
            icon: GraduationCap,
          },
          {
            label: t('riggerForHire'),
            description: t('riggerForHireDescription'),
            href: '/rigger-for-hire',
            icon: Clapperboard,
          },
          {
            label: t('myContent'),
            description: t('myContentDescription'),
            href: '/my-content',
            icon: Folder,
          },
          {
            label: t('memberships'),
            description: t('membershipsDescription'),
            href: '/memberships',
            icon: CreditCard,
          },
          {
            label: t('affiliateProgram'),
            description: t('affiliateProgramDescription'),
            href: '/affiliates',
            icon: Handshake,
          },
          {
            label: t('models'),
            description: t('modelsDescription'),
            href: '/models',
            icon: Users,
          },
          {
            label: t('liveClasses'),
            description: t('liveClassesDescription'),
            href: '/live-classes',
            icon: Presentation,
          },
          {
            label: t('voteOnContent'),
            description: t('voteOnContentDescription'),
            href: '/vote-on-new-content',
            icon: Vote,
          },
        ],
      },
      noSkeleton: true,
    },
  ]
}

// Map nav labels to content types for featured content fetching
const NAV_CONTENT_TYPE_MAP: Record<string, string> = {
  Tutorials: 'tutorial',
  'Creating a Scene': 'cas',
  Floggers: 'floggerDesign',
  Courses: 'course',
  Blog: 'post',
}

interface SiteHeaderProps {
  /**
   * Whether to render the inline language switcher in the right-side
   * cluster (next to search + auth). Driven by the
   * `language_selector_placement` setting on the siteSettings doc;
   * the floating variants are rendered by the layout instead.
   */
  showLanguageSwitcher?: boolean
  /**
   * Server-prefetched data for the four Sanity-driven mega menu panels
   * (Tutorials, Creating a Scene, Floggers, Store). When provided, each
   * panel renders with its data already in place instead of waiting for
   * a client-side `useEffect` round-trip on first hover. If absent (or
   * for a panel that prefetched as `null` due to a transient error), the
   * panel falls back to its existing client fetch.
   */
  megaMenuData?: import('@/lib/sanity/mega-menu-data').MegaMenuPrefetchedData
  /**
   * Auth state resolved server-side by the layout and passed in so the
   * header renders the correct auth cluster (account icon vs.
   * Login / Try Free) on the very first paint. Without this the header
   * mounts with `user = null`, shows the logged-out buttons, then
   * swaps to the account icon once the client-side `getUser()` resolves
   * — the "flash of Join Now" a logged-in user sees on navigation.
   * `null` = confirmed logged out; `undefined` = layout didn't provide
   * it (falls back to the old client-fetch-only behaviour).
   */
  initialUser?: SupabaseUser | null
}

export function SiteHeader({ showLanguageSwitcher = false, megaMenuData, initialUser = null }: SiteHeaderProps = {}) {
  const t = useTranslations('nav')
  const tNav = useTranslations('navMenu')
  const navItems = buildNavItems(tNav)
  const router = useRouter()
  const [activeMenu, setActiveMenu] = useState<string | null>(null)
  // Once a panel has been hovered we keep it in the DOM (hidden via display:none
  // when it's not the active menu). That way each panel's data fetch runs at
  // most once per page load instead of every time the user re-opens the same
  // menu — which is what made image thumbnails appear with a noticeable delay.
  const [mountedMenus, setMountedMenus] = useState<Set<string>>(new Set())
  const [mobileOpen, setMobileOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  // Seed from the server-resolved user so the first paint already
  // shows the right auth cluster — see the `initialUser` prop doc.
  const [user, setUser] = useState<SupabaseUser | null>(initialUser)
  const [avatarUrl, setAvatarUrl] = useState<string | null>(
    initialUser?.user_metadata?.avatar_url ||
      initialUser?.user_metadata?.picture ||
      null,
  )
  const [featuredItems, setFeaturedItems] = useState<Record<string, NavItem['panel'] extends undefined ? never : NonNullable<NavItem['panel']>['featured']>>({})
  const [peekResults, setPeekResults] = useState<QuickPeekResult[]>([])
  const [peekLoading, setPeekLoading] = useState(false)
  const peekDebounceRef = useRef<NodeJS.Timeout | null>(null)
  const navRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const openTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  // The disclosure button that last opened a mega-menu panel, so Escape (from
  // the trigger or from inside the panel) can return focus to it (WCAG 2.1.1 /
  // 2.4.3). Stable DOM id for each panel so the trigger's aria-controls points
  // at it (4.1.2).
  const lastTriggerRef = useRef<HTMLButtonElement | null>(null)
  // True when a mega-menu was opened via its disclosure BUTTON (keyboard or
  // click) so the effect below can move focus INTO the panel. Mouse hover-open
  // never sets it, so hovering never steals focus.
  const openedByButtonRef = useRef(false)

  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      router.push(`/search?q=${encodeURIComponent(searchQuery.trim())}`)
      setSearchOpen(false)
      setSearchQuery('')
      setPeekResults([])
    }
  }, [searchQuery, router])

  // Debounced quick-peek search
  useEffect(() => {
    if (peekDebounceRef.current) clearTimeout(peekDebounceRef.current)

    const q = searchQuery.trim()
    if (q.length < 2) {
      setPeekResults([])
      setPeekLoading(false)
      return
    }

    setPeekLoading(true)
    peekDebounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/search?q=${encodeURIComponent(q)}`)
        if (res.ok) {
          const data = await res.json()
          setPeekResults(data.results || [])
        }
      } catch {
        // Silently fail
      } finally {
        setPeekLoading(false)
      }
    }, 250)

    return () => {
      if (peekDebounceRef.current) clearTimeout(peekDebounceRef.current)
    }
  }, [searchQuery])

  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus()
    }
    if (!searchOpen) {
      setPeekResults([])
    }
  }, [searchOpen])

  // Fetch featured content for mega menu panels
  useEffect(() => {
    const fetchFeatured = async () => {
      const supabase = createClient()
      const results: Record<string, NonNullable<NonNullable<NavItem['panel']>['featured']>> = {}

      for (const [label, type] of Object.entries(NAV_CONTENT_TYPE_MAP)) {
        try {
          // Blog gets a special Sanity fetch so we can include blog_type
          if (type === 'post') {
            const res = await fetch(
              `/api/sanity/blog-featured`,
              { credentials: 'include' },
            )
            if (res.ok) {
              const posts = await res.json()
              if (posts.length > 0) results[label] = posts
            }
            continue
          }

          const { data, error } = await supabase
            .from('content_index')
            .select('title, slug, type, thumbnail_url, is_gated')
            .eq('type', type)
            .order('created_at', { ascending: false })
            .limit(5)

          if (error || !data || data.length === 0) continue

          const typeLabels: Record<string, string> = {
            tutorial: 'Tutorial',
            cas: 'Scene',
            flogger: 'Flogger',
            course: 'Course',
            lesson: 'Lesson',
          }

          const items = data.map((item) => {
            let title = String(item.title)
            try {
              const parsed = typeof item.title === 'string' ? JSON.parse(item.title) : item.title
              if (parsed && typeof parsed === 'object' && 'en' in parsed) {
                title = String(parsed.en)
              }
            } catch {
              // title is already a plain string
            }

            return {
              title,
              slug: item.slug,
              href: `/${item.type === 'cas' ? 'cas' : item.type + 's'}/${item.slug}`,
              // Null passes through unchanged (was untyped); MegaMenuPanel only
              // truthiness-checks `thumbnail`, so null behaves like undefined.
              thumbnail: item.thumbnail_url as string | undefined,
              isGated: item.is_gated ?? false,
              category: typeLabels[item.type] || item.type,
            }
          })

          if (items.length > 0) {
            results[label] = items
          }
        } catch {
          // Silent fail — featured items are optional
        }
      }
      setFeaturedItems(results)
    }
    fetchFeatured()
  }, [])

  // Fetch auth state
  useEffect(() => {
    const supabase = createClient()
    getVerifiedClaims(supabase).then((claims) => {
      const user = claims ? claimsToUserShape(claims) : null
      setUser(user)
      if (user) {
        setAvatarUrl(user.user_metadata?.avatar_url || user.user_metadata?.picture || null)
      }
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null)
      if (session?.user) {
        setAvatarUrl(session.user.user_metadata?.avatar_url || session.user.user_metadata?.picture || null)
      } else {
        setAvatarUrl(null)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const openMenu = (label: string) => {
    setActiveMenu(label)
    setMountedMenus((prev) => (prev.has(label) ? prev : new Set(prev).add(label)))
  }

  const handleMenuEnter = (label: string) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
    // Cancel any pending open for a different item.
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current)
      openTimeoutRef.current = null
    }
    // If a menu is already open, switch to this one instantly — the user is
    // already in the nav bar. Otherwise wait out a short hover-intent delay so
    // merely dragging the pointer across a nav item doesn't pop the mega menu.
    if (activeMenu) {
      openMenu(label)
    } else {
      openTimeoutRef.current = setTimeout(() => {
        openTimeoutRef.current = null
        openMenu(label)
      }, 450)
    }
  }

  const handleMenuLeave = () => {
    // Cancel a pending open so a quick pass-through never opens the menu.
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current)
      openTimeoutRef.current = null
    }
    closeTimeoutRef.current = setTimeout(() => {
      setActiveMenu(null)
    }, 150) // Small delay so user can move to the panel
  }

  const handlePanelEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
  }

  const handlePanelLeave = () => {
    setActiveMenu(null)
  }

  // Keyboard disclosure: the chevron button toggles its panel open/closed
  // (mouse users still get hover-open above). Cancels any pending hover timers
  // so a keyboard toggle wins cleanly.
  const toggleMenu = (label: string) => {
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current)
      openTimeoutRef.current = null
    }
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
    if (activeMenu === label) {
      setActiveMenu(null)
    } else {
      openMenu(label)
    }
  }

  // Escape closes the open mega-menu/dropdown and returns focus to its trigger
  // button. Wired on both the nav-item wrapper (focus on the trigger/link) and
  // the panel wrapper (focus inside the panel, which renders outside the item).
  const handleMenuKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && activeMenu) {
      e.stopPropagation()
      setActiveMenu(null)
      lastTriggerRef.current?.focus()
    }
  }

  // Stable DOM id for a panel, so its trigger's aria-controls matches.
  const panelDomId = (label: string) =>
    `mega-panel-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`

  // When a panel is opened via its disclosure button, move focus INTO the panel
  // so keyboard users land in the revealed content — the mega panels render far
  // down the header DOM, so a plain Tab would skip past them. Escape returns
  // focus to the trigger (handleMenuKeyDown). Hover-open is unaffected.
  useEffect(() => {
    if (!activeMenu || !openedByButtonRef.current) return
    openedByButtonRef.current = false
    const id = `mega-panel-${activeMenu.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
    // rAF so the panel (and its lazily-mounted content) is laid out first.
    const raf = requestAnimationFrame(() => {
      const panel = document.getElementById(id)
      if (!panel) return
      const target =
        panel.querySelector<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) || panel
      target.focus()
    })
    return () => cancelAnimationFrame(raf)
  }, [activeMenu])

  return (
    <>
    <header className="sticky top-0 z-50 w-full border-b bg-white dark:bg-gray-950">
      {/* Desktop Nav */}
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4">
        {/* Logo */}
        <Link href="/" className="flex items-center shrink-0">
          <Image
            src="/logo.webp"
            alt="TheDuchy"
            width={40}
            height={40}
            className="h-10 w-10"
            priority
          />
        </Link>

        {/* Desktop: Full-width search overlay OR nav + auth */}
        {searchOpen ? (
          /* Search overlay — replaces nav and auth when open */
          <div className="hidden lg:flex flex-1 items-center gap-3 ml-4 relative">
            <form onSubmit={handleSearch} className="flex-1 relative">
              <input
                ref={searchInputRef}
                type="text"
                placeholder={t('searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape') { setSearchOpen(false); setSearchQuery(''); setPeekResults([]) } }}
                className="w-full border border-input bg-gray-50 py-2 pl-4 pr-4 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
                autoFocus
              />

              {/* Quick-peek results dropdown */}
              {(peekResults.length > 0 || peekLoading) && searchQuery.trim().length >= 2 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-lg z-[60] overflow-hidden">
                  {peekLoading && peekResults.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-gray-400">{t('searching')}</div>
                  ) : (
                    <>
                      {peekResults.map((result) => (
                        <Link
                          key={result.id}
                          href={peekResultHref(result.type, result.slug)}
                          onClick={() => { setSearchOpen(false); setSearchQuery(''); setPeekResults([]) }}
                          className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                        >
                          <div className="shrink-0 w-10 h-10 overflow-hidden bg-gray-100 dark:bg-gray-800">
                            {result.thumbnailUrl ? (
                              <img src={result.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-300 dark:text-gray-600">
                                <Search className="h-4 w-4" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {result.title}
                            </p>
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs text-brand-600 dark:text-brand-400">
                                {PEEK_TYPE_LABELS[result.type] || result.type}
                              </span>
                              {result.isGated && user?.app_metadata?.has_access !== true && (
                                <Lock className="h-3 w-3 text-amber-500" />
                              )}
                            </div>
                          </div>
                        </Link>
                      ))}
                      <Link
                        href={`/search?q=${encodeURIComponent(searchQuery.trim())}`}
                        onClick={() => { setSearchOpen(false); setSearchQuery(''); setPeekResults([]) }}
                        className="block px-4 py-2.5 text-sm text-center font-medium text-brand-700 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/30 border-t border-gray-100 dark:border-gray-800 transition-colors"
                      >
                        View all results →
                      </Link>
                    </>
                  )}
                </div>
              )}
            </form>
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('searchClose')}
              onClick={() => { setSearchOpen(false); setSearchQuery(''); setPeekResults([]) }}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        ) : (
          <>
            {/* Desktop Menu Items */}
            <nav
              ref={navRef}
              className="hidden lg:flex items-center gap-0.5"
            >
              {navItems.map((item) => (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => (item.panel || item.dropdown) ? handleMenuEnter(item.label) : setActiveMenu(null)}
                  onMouseLeave={handleMenuLeave}
                  onKeyDown={handleMenuKeyDown}
                >
                  <div className="flex items-center">
                    <Link
                      href={item.href}
                      onClick={() => setActiveMenu(null)}
                      className={`flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors ${
                        activeMenu === item.label
                          ? 'text-brand-700 bg-brand-50 dark:bg-brand-900'
                          : 'text-gray-700 hover:text-brand-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-900'
                      }`}
                    >
                      {item.label}
                    </Link>
                    {/* Disclosure toggle for the panel/dropdown. A separate
                        button (not the Link) so keyboard users can OPEN the menu
                        without navigating, with proper aria-expanded/haspopup/
                        controls state (WCAG 2.1.1 / 4.1.2). Mouse hover still
                        opens via the wrapper handlers above. */}
                    {(item.panel || item.dropdown) && (
                      <button
                        type="button"
                        aria-label={`${item.label} menu`}
                        aria-haspopup="true"
                        aria-expanded={activeMenu === item.label}
                        aria-controls={panelDomId(item.label)}
                        onClick={(e) => {
                          lastTriggerRef.current = e.currentTarget
                          openedByButtonRef.current = true
                          toggleMenu(item.label)
                        }}
                        className={`flex items-center rounded px-1.5 py-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
                          activeMenu === item.label
                            ? 'text-brand-700 dark:text-brand-300'
                            : 'text-gray-500 hover:text-brand-700 dark:text-gray-400'
                        }`}
                      >
                        <ChevronDown className={`h-3 w-3 transition-transform ${activeMenu === item.label ? 'rotate-180' : ''}`} />
                      </button>
                    )}
                  </div>
                  {/* Simple dropdown for Resources */}
                  {item.dropdown && activeMenu === item.label && (
                    <div id={panelDomId(item.label)} className="absolute top-full left-0 z-50 min-w-48 border bg-white shadow-lg py-1">
                      {item.dropdown.map((link) => (
                        <Link
                          key={link.href}
                          href={link.href}
                          onClick={() => setActiveMenu(null)}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-brand-700"
                        >
                          {link.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* Right side: Language + Search + Auth */}
            <div className="hidden lg:flex items-center gap-2 shrink-0">
              {showLanguageSwitcher && <LanguageSwitcher variant="header" />}
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('search')}
                onClick={() => setSearchOpen(true)}
              >
                <Search className="h-5 w-5" />
              </Button>

              {user ? (
                <Link href="/account" className="flex items-center gap-2" aria-label={t('myAccount')}>
                  <div className="h-8 w-8 overflow-hidden border-2 border-gray-200 hover:border-brand-500 transition-colors">
                    {avatarUrl ? (
                      <img src={avatarUrl} alt={t('myAccount')} className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-brand-100 text-brand-700">
                        <User className="h-4 w-4" />
                      </div>
                    )}
                  </div>
                </Link>
              ) : (
                <>
                  <Link href="/auth">
                    <Button variant="ghost" size="sm">{t('login')}</Button>
                  </Link>
                  <Link href="/auth?mode=register">
                    <Button size="sm" className="bg-brand-700 hover:bg-brand-800 text-white">
                      {t('tryFree')}
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </>
        )}

        {/* Mobile: Hamburger */}
        <div className="flex lg:hidden items-center gap-2">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <button
              onClick={() => setMobileOpen(true)}
              className="inline-flex items-center justify-center p-2 text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800"
              aria-label="Menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            {/* initialFocus=false: don't auto-focus the first focusable element
                (the search box) on open — that pops the mobile keyboard and
                covers half the menu. Focus stays on the trigger; the user taps
                search manually if they want it. */}
            <SheetContent side="right" className="w-80 overflow-y-auto p-0" initialFocus={false}>
              <SheetTitle className="sr-only">Navigation</SheetTitle>

              {/* ─── Header with logo ─────────────────────────────── */}
              <div className="flex items-center gap-3 border-b px-5 py-4">
                <Image src="/logo.webp" alt="TheDuchy" width={32} height={32} className="h-8 w-8" />
                <span className="text-base font-bold tracking-tight text-gray-900 dark:text-white">TheDuchy</span>
              </div>

              {/* ─── Search ───────────────────────────────────────── */}
              <div className="px-5 pt-4 pb-2">
                <form
                  onSubmit={(e) => { e.preventDefault(); if (searchQuery.trim()) { router.push(`/search?q=${encodeURIComponent(searchQuery.trim())}`); setMobileOpen(false); setSearchQuery(''); setPeekResults([]) } }}
                >
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder={t('searchPlaceholderShort')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full border border-input bg-gray-50 py-2.5 pl-10 pr-4 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100 dark:bg-gray-800 dark:text-white"
                    />
                  </div>

                  {/* Quick-peek results */}
                  {(peekResults.length > 0 || peekLoading) && searchQuery.trim().length >= 2 && (
                    <div className="mt-2 border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-900 shadow-sm">
                      {peekLoading && peekResults.length === 0 ? (
                        <div className="px-3 py-3 text-sm text-gray-400">{t('searching')}</div>
                      ) : (
                        <>
                          {peekResults.map((result) => (
                            <Link
                              key={result.id}
                              href={peekResultHref(result.type, result.slug)}
                              onClick={() => { setMobileOpen(false); setSearchQuery(''); setPeekResults([]) }}
                              className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                            >
                              <div className="shrink-0 w-9 h-9 overflow-hidden bg-gray-100 dark:bg-gray-800">
                                {result.thumbnailUrl ? (
                                  <img src={result.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-gray-300 dark:text-gray-600">
                                    <Search className="h-3.5 w-3.5" />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                  {result.title}
                                </p>
                                <div className="flex items-center gap-1.5">
                                  <span className="text-xs text-brand-600 dark:text-brand-400">
                                    {PEEK_TYPE_LABELS[result.type] || result.type}
                                  </span>
                                  {result.isGated && (
                                    <Lock className="h-3 w-3 text-amber-500" />
                                  )}
                                </div>
                              </div>
                            </Link>
                          ))}
                          <Link
                            href={`/search?q=${encodeURIComponent(searchQuery.trim())}`}
                            onClick={() => { setMobileOpen(false); setSearchQuery(''); setPeekResults([]) }}
                            className="block px-3 py-2.5 text-sm text-center font-medium text-brand-700 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/30 border-t border-gray-100 dark:border-gray-800 transition-colors"
                          >
                            View all results &rarr;
                          </Link>
                        </>
                      )}
                    </div>
                  )}
                </form>
              </div>

              {/* ─── Auth ─────────────────────────────────────────── */}
              <div className="px-5 pb-4">
                {user ? (
                  <Link href="/account" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 bg-gray-50 dark:bg-gray-800/60 p-3 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800">
                    <div className="h-10 w-10 overflow-hidden bg-brand-100 dark:bg-brand-900/40 shrink-0">
                      {avatarUrl ? (
                        <img src={avatarUrl} alt="Avatar" className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-brand-700 dark:text-brand-400">
                          <User className="h-5 w-5" />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">{t('myAccount')}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
                    </div>
                    <ChevronRight className="h-4 w-4 ml-auto text-gray-400 shrink-0" />
                  </Link>
                ) : (
                  <div className="flex gap-2">
                    <Link href="/auth" onClick={() => setMobileOpen(false)} className="flex-1">
                      <Button variant="outline" className="w-full h-10">{t('login')}</Button>
                    </Link>
                    <Link href="/auth?mode=register" onClick={() => setMobileOpen(false)} className="flex-1">
                      <Button className="w-full h-10 bg-brand-700 hover:bg-brand-800 text-white font-semibold">
                        {t('tryFree')}
                      </Button>
                    </Link>
                  </div>
                )}
              </div>

              {/* ─── Divider ──────────────────────────────────────── */}
              <div className="border-t border-gray-100 dark:border-gray-800" />

              {/* ─── Navigation ────────────────────────────────────── */}
              <div className="px-3 py-2">
                <Accordion className="w-full">
                  {navItems.map((item) => (
                    item.panel ? (
                      <AccordionItem key={item.label} value={item.label} className="border-none">
                        <AccordionTrigger className="px-2 py-3 text-sm font-semibold text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800/60 hover:no-underline [&>svg]:h-4 [&>svg]:w-4 [&>svg]:text-gray-400">
                          {item.label}
                        </AccordionTrigger>
                        <AccordionContent className="pb-1">
                          <div className="flex flex-col gap-0.5 pl-2">
                            {/* View All — pinned to the TOP of each section */}
                            <Link
                              href={item.href}
                              className="px-3 py-2 text-sm font-medium text-brand-700 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/30 transition-colors"
                              onClick={() => setMobileOpen(false)}
                            >
                              View All {item.label} &rarr;
                            </Link>

                            {/* Sections layout (Tutorials) */}
                            {item.panel.sections && item.panel.sections.length > 0 ? (
                              <>
                                {item.panel.sections.map((section) => (
                                  <div key={section.label} className="mt-2 first:mt-0">
                                    <p className="px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">
                                      {section.label}
                                    </p>
                                    {section.items.map((si) => (
                                      <Link
                                        key={si.label}
                                        href={si.href}
                                        className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/60 hover:text-gray-900 dark:hover:text-white transition-colors"
                                        onClick={() => setMobileOpen(false)}
                                      >
                                        {si.color && (
                                          <span className="h-2 w-2 shrink-0" style={{ background: si.color }} />
                                        )}
                                        {si.label}
                                      </Link>
                                    ))}
                                  </div>
                                ))}
                              </>
                            ) : item.panel.columns && item.panel.columns.length > 0 ? (
                              /* Difficulty columns (Creating a Scene, Courses) */
                              item.panel.columns.map((col) => (
                                <Link
                                  key={col.key}
                                  href={col.href}
                                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/60 hover:text-gray-900 dark:hover:text-white transition-colors"
                                  onClick={() => setMobileOpen(false)}
                                >
                                  <span className="h-2 w-2 shrink-0" style={{ background: col.color }} />
                                  {col.label}
                                </Link>
                              ))
                            ) : item.panel.categories && item.panel.categories.length > 0 ? (
                              /* Category links + description (Blog) */
                              item.panel.categories.map((cat) => (
                                <Link
                                  key={cat.label}
                                  href={cat.href}
                                  className="flex flex-col px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors"
                                  onClick={() => setMobileOpen(false)}
                                >
                                  <span className="text-sm text-gray-700 dark:text-gray-300">{cat.label}</span>
                                  {cat.description && (
                                    <span className="text-xs text-gray-400 dark:text-gray-500">{cat.description}</span>
                                  )}
                                </Link>
                              ))
                            ) : item.panel.cards && item.panel.cards.length > 0 ? (
                              /* Icon + label cards (Community, Resources) */
                              item.panel.cards.map((card) => {
                                const Icon = card.icon
                                return (
                                  <Link
                                    key={card.label}
                                    href={card.href}
                                    className="flex items-center gap-2.5 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/60 hover:text-gray-900 dark:hover:text-white transition-colors"
                                    onClick={() => setMobileOpen(false)}
                                  >
                                    {Icon && <Icon className="h-4 w-4 shrink-0 text-gray-400" />}
                                    {card.label}
                                  </Link>
                                )
                              })
                            ) : (
                              /* Default flat tabs (Store, Floggers) */
                              item.panel.tabs.map((tab) => (
                                <Link
                                  key={tab.label}
                                  href={tab.href}
                                  className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/60 hover:text-gray-900 dark:hover:text-white transition-colors"
                                  onClick={() => setMobileOpen(false)}
                                >
                                  {tab.label}
                                </Link>
                              ))
                            )}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ) : (
                      <div key={item.label}>
                        <Link
                          href={item.href}
                          className="flex items-center px-2 py-3 text-sm font-semibold text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors"
                          onClick={() => setMobileOpen(false)}
                        >
                          {item.label}
                        </Link>
                      </div>
                    )
                  ))}
                </Accordion>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Desktop Mega Menu Panel — positioned directly under the nav bar.
          Once a panel has been opened we keep it mounted (hidden via display:none
          when inactive) so its data fetch runs at most once per page. The wrapper
          itself is rendered as soon as any panel has been mounted but stays
          pointer-events-none when no menu is active so it doesn't block the page. */}
      {mountedMenus.size > 0 && (
        <div
          ref={panelRef}
          className={`hidden lg:block absolute left-0 right-0 top-[65px] z-40 ${activeMenu ? '' : 'pointer-events-none'}`}
          onMouseEnter={handlePanelEnter}
          onMouseLeave={handlePanelLeave}
          onKeyDown={handleMenuKeyDown}
        >
          {navItems.map((item) => {
            if (!item.panel) return null
            if (!mountedMenus.has(item.label)) return null
            const isActive = activeMenu === item.label
            return (
              <div
                key={item.label}
                id={panelDomId(item.label)}
                // Focusable fallback: when opened by keyboard, focus moves to the
                // first link inside, or to this container if the panel is still
                // showing its loading skeleton (tabIndex -1 = programmatic only).
                tabIndex={-1}
                style={{ display: isActive ? 'block' : 'none' }}
                // `inert` (not `aria-hidden`) is the right primitive when the
                // panel may briefly hold focus during a hover→close transition
                // — `aria-hidden` on a focused ancestor triggers a Chrome
                // accessibility warning. `inert` removes the subtree from
                // both the accessibility tree AND the focus ring, and blurs
                // any descendant that already had focus.
                inert={!isActive}
              >
                <MegaMenuPanel
                  label={item.label}
                  href={item.href}
                  tabs={item.panel.tabs}
                  featured={featuredItems[item.label] || item.panel.featured}
                  banners={item.panel.banners}
                  categories={item.panel.categories}
                  columns={item.panel.columns}
                  sections={item.panel.sections}
                  cards={item.panel.cards}
                  floggerLayout={item.panel.floggerLayout}
                  storeLayout={item.panel.storeLayout}
                  coursesLayout={item.panel.coursesLayout}
                  showSkeleton={!item.noSkeleton}
                  hasAccess={user?.app_metadata?.has_access === true}
                  onNavigate={() => setActiveMenu(null)}
                  prefetchedTutorials={megaMenuData?.tutorials}
                  prefetchedCas={megaMenuData?.cas}
                  prefetchedFloggers={megaMenuData?.floggers}
                  prefetchedStore={megaMenuData?.store}
                />
              </div>
            )
          })}
        </div>
      )}
    </header>
    </>
  )
}
